"use server";
import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";
import * as client from "openid-client";
import { auth } from "@/server/auth";
import { destroySession } from "@/server/auth/session";
import {
    getOidcConfig,
    redirectUri,
    setLoginTransientCookie,
} from "@/server/auth/oidc";
import { sanitizeReturnTo } from "@/server/auth/transient-crypto";
import { env } from "@/env";

export async function revalidateAppCache() {
    revalidatePath('/', 'layout')
}

export async function revalidateSession() {
    revalidatePath('/api/auth/session')
}

export async function signInLogto(returnTo?: string) {
    const config = getOidcConfig();
    const verifier = client.randomPKCECodeVerifier();
    const code_challenge = await client.calculatePKCECodeChallenge(verifier);
    const state = client.randomState();
    const nonce = client.randomNonce();
    // The destination rides in the sealed handshake cookie, so nothing on the
    // wire can change where the user lands after the IdP round trip.
    await setLoginTransientCookie({
        state,
        nonce,
        verifier,
        returnTo: sanitizeReturnTo(returnTo) ?? undefined,
    });
    const authorizationUrl = client.buildAuthorizationUrl(config, {
        redirect_uri: redirectUri(),
        scope: "openid profile email",
        code_challenge,
        code_challenge_method: "S256",
        state,
        nonce,
    });
    redirect(authorizationUrl.toString());
}

export async function signOutLocal() {
    // No redirect here: the caller (client signOut) hard-navigates after the
    // action resolves, which also resets client state a soft navigation
    // would keep (session provider, jotai atoms).
    await destroySession();
}

export async function signOutEverywhere() {
    // "Sign out everywhere" (KUORI_AUTH_ARCHITECTURE.md §10): destroy the
    // local session, then RP-initiated logout at the IdP so its SSO cookie
    // dies too and the next sign-in prompts for credentials again. Plain
    // sign-out leaves the IdP session alive, so the next sign-in returns
    // silently.
    await destroySession();
    const endSessionUrl = client.buildEndSessionUrl(getOidcConfig(), {
        // Must exactly match a postLogoutRedirectUri registered for the DSM
        // client in Logto: the application origin with a trailing slash.
        post_logout_redirect_uri: new URL(env.AUTH_URL).origin + "/",
    });
    redirect(endSessionUrl.toString());
}

export async function useServerSession() {
    const session = await auth();
    return session;
}
